// lib/docKinds.js
// Source of truth for document kinds & field requirements.

export const DOC_KINDS = [
  // === IDENTITY DOCUMENTS (Person) ===
  { key: "passport", label: "Passport", target: "person", category: "identity" },
  { key: "id", label: "National ID", target: "person", category: "identity" },
  { key: "drivers_license", label: "Driver's License", target: "person", category: "identity" },
  { key: "residence_permit", label: "Residence Permit", target: "person", category: "identity" },

  // === COMPANY CERTIFICATES (No expiry) ===
  { key: "coc_extract", label: "Chamber of Commerce Extract", target: "company", category: "certificate" },
  { key: "articles_of_association", label: "Articles of Association", target: "company", category: "certificate" },
  { key: "certificate_of_incorporation", label: "Certificate of Incorporation", target: "company", category: "certificate" },
  { key: "certificate_of_good_standing", label: "Certificate of Good Standing", target: "company", category: "certificate" },
  { key: "tax_certificate", label: "Tax Certificate", target: "company", category: "certificate" },

  // === AGREEMENTS (Period-based) ===
  { key: "management_agreement", label: "Management Agreement", target: "company", category: "agreement" },
  { key: "service_agreement", label: "Service Agreement", target: "company", category: "agreement" },
  { key: "shareholder_agreement", label: "Shareholder Agreement", target: "company", category: "agreement" },
  { key: "lease_agreement", label: "Lease Agreement", target: "company", category: "agreement" },

  // === FINANCIAL DOCUMENTS ===
  { key: "bank_statement", label: "Bank Statement", target: "company", category: "financial" },
  { key: "financial_statement", label: "Financial Statement", target: "company", category: "financial" },
  { key: "audit_report", label: "Audit Report", target: "company", category: "financial" },
  { key: "invoice", label: "Invoice", target: "company", category: "financial" },

  // === LICENSES & PERMITS (May expire) ===
  { key: "business_license", label: "Business License", target: "company", category: "license" },
  { key: "gaming_license", label: "Gaming License", target: "company", category: "license" },
  { key: "operating_permit", label: "Operating Permit", target: "company", category: "license" },
  { key: "environmental_permit", label: "Environmental Permit", target: "company", category: "license" },

  // === GOVERNANCE ===
  { key: "board_resolution", label: "Board Resolution", target: "company", category: "governance" },
  { key: "shareholder_resolution", label: "Shareholder Resolution", target: "company", category: "governance" },
  { key: "meeting_minutes", label: "Meeting Minutes", target: "company", category: "governance" },
  { key: "power_of_attorney", label: "Power of Attorney", target: "both", category: "governance" },

  // === COMPLIANCE ===
  { key: "aml_screening", label: "AML Screening Report", target: "both", category: "compliance" },
  { key: "kyc_form", label: "KYC Form", target: "both", category: "compliance" },
  { key: "source_of_funds", label: "Source of Funds Declaration", target: "both", category: "compliance" },
  { key: "pep_declaration", label: "PEP Declaration", target: "person", category: "compliance" },

  // === CUSTOM ===
  { key: "custom", label: "Custom Document", target: "both", category: "custom" },
];

export const DOC_STATUS_OPTIONS = [
  "received", // uploaded but not sent out
  "submitted", // sent to external authority
  "in_review", // being reviewed by authority
  "approved", // accepted/valid
  "rejected", // denied/invalid
  "expired", // past validity date
];

// Field visibility rules per document kind
// Shows ONLY relevant fields based on document type
export const DOC_KIND_FIELDS = {
  // === IDENTITY DOCUMENTS (Strict) ===
  passport: {
    required: ["identifier", "issued_on", "expires_on"],
    optional: ["authority", "status", "note", "file"],
    target: "person",
  },
  id: {
    required: ["identifier", "issued_on", "expires_on"],
    optional: ["authority", "status", "note", "file"],
    target: "person",
  },
  drivers_license: {
    required: ["identifier", "issued_on", "expires_on"],
    optional: ["authority", "status", "note", "file"],
    target: "person",
  },
  residence_permit: {
    required: ["identifier", "issued_on", "expires_on"],
    optional: ["authority", "status", "note", "file"],
    target: "person",
  },

  // === COMPANY CERTIFICATES (No expiry, issued_on only) ===
  coc_extract: {
    required: ["issued_on"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },
  articles_of_association: {
    required: ["issued_on"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },
  certificate_of_incorporation: {
    required: ["issued_on"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },
  certificate_of_good_standing: {
    required: ["issued_on"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },
  tax_certificate: {
    required: ["issued_on"],
    optional: ["identifier", "expires_on", "status", "note", "file"],
    target: "company",
  },

  // === AGREEMENTS (Period-based, no issued_on) ===
  management_agreement: {
    required: ["period_from", "period_to"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },
  service_agreement: {
    required: ["period_from", "period_to"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },
  shareholder_agreement: {
    required: ["period_from"],
    optional: ["period_to", "identifier", "status", "note", "file"],
    target: "company",
  },
  lease_agreement: {
    required: ["period_from", "period_to"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },

  // === FINANCIAL DOCUMENTS (Period-based) ===
  bank_statement: {
    required: ["period_from", "period_to"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },
  financial_statement: {
    required: ["period_from", "period_to"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },
  audit_report: {
    required: ["issued_on"],
    optional: ["period_from", "period_to", "identifier", "status", "note", "file"],
    target: "company",
  },
  invoice: {
    required: ["issued_on"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },

  // === LICENSES & PERMITS (May expire) ===
  business_license: {
    required: ["identifier", "issued_on"],
    optional: ["expires_on", "authority", "status", "note", "file"],
    target: "company",
  },
  gaming_license: {
    required: ["identifier", "issued_on"],
    optional: ["expires_on", "authority", "status", "note", "file"],
    target: "company",
  },
  operating_permit: {
    required: ["identifier", "issued_on"],
    optional: ["expires_on", "authority", "status", "note", "file"],
    target: "company",
  },
  environmental_permit: {
    required: ["identifier", "issued_on"],
    optional: ["expires_on", "authority", "status", "note", "file"],
    target: "company",
  },

  // === GOVERNANCE (Date-based) ===
  board_resolution: {
    required: ["issued_on"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },
  shareholder_resolution: {
    required: ["issued_on"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },
  meeting_minutes: {
    required: ["issued_on"],
    optional: ["identifier", "status", "note", "file"],
    target: "company",
  },
  power_of_attorney: {
    required: ["issued_on"],
    optional: ["expires_on", "identifier", "status", "note", "file"],
    target: "both",
  },

  // === COMPLIANCE ===
  aml_screening: {
    required: ["issued_on"],
    optional: ["expires_on", "identifier", "status", "note", "file"],
    target: "both",
  },
  kyc_form: {
    required: ["issued_on"],
    optional: ["identifier", "status", "note", "file"],
    target: "both",
  },
  source_of_funds: {
    required: ["issued_on"],
    optional: ["identifier", "status", "note", "file"],
    target: "both",
  },
  pep_declaration: {
    required: ["issued_on"],
    optional: ["identifier", "status", "note", "file"],
    target: "person",
  },

  // === CUSTOM (Minimal requirements) ===
  custom: {
    required: ["kind_label"],
    optional: ["identifier", "issued_on", "expires_on", "period_from", "period_to", "authority", "status", "note", "file"],
    target: "both",
  },
};

// Expiry policies - determines auto-calculation behavior
export const DOC_EXPIRY_POLICIES = {
  // Identity docs - always expire
  passport: {
    expires: true,
    autoYears: 10,
    overrides: {
      byCountry: {
        CW: { autoYears: 10 },
        NL: { autoYears: 10 },
        US: { autoYears: 10 },
      },
    },
  },
  id: {
    expires: true,
    autoYears: 10,
    overrides: {
      byCountry: {
        CW: { autoYears: 5 },
        NL: { autoYears: 10 },
      },
    },
  },
  drivers_license: {
    expires: true,
    autoYears: 10,
  },
  residence_permit: {
    expires: true,
    autoYears: 1,
  },

  // Certificates - NEVER expire
  coc_extract: { expires: false },
  articles_of_association: { expires: false },
  certificate_of_incorporation: { expires: false },
  certificate_of_good_standing: { expires: false },
  tax_certificate: { expires: true, autoYears: 1 },

  // Agreements - period-based (not issued/expires)
  management_agreement: { expires: true, field: "period_to", source: "period_from", autoYears: 1 },
  service_agreement: { expires: true, field: "period_to", source: "period_from", autoYears: 1 },
  shareholder_agreement: { expires: true, field: "period_to", source: "period_from", autoYears: 99 }, // Perpetual by default
  lease_agreement: { expires: true, field: "period_to", source: "period_from", autoYears: 1 },

  // Financial - period-based
  bank_statement: { expires: true, field: "period_to", source: "period_from", autoMonths: 1 },
  financial_statement: { expires: true, field: "period_to", source: "period_from", autoYears: 1 },
  audit_report: { expires: false },
  invoice: { expires: false },

  // Licenses - may expire
  business_license: { expires: true, autoYears: 1 },
  gaming_license: { expires: true, autoYears: 5 },
  operating_permit: { expires: true, autoYears: 1 },
  environmental_permit: { expires: true, autoYears: 1 },

  // Governance - no expiry
  board_resolution: { expires: false },
  shareholder_resolution: { expires: false },
  meeting_minutes: { expires: false },
  power_of_attorney: { expires: true, autoYears: 1 },

  // Compliance
  aml_screening: { expires: true, autoYears: 1 },
  kyc_form: { expires: false },
  source_of_funds: { expires: false },
  pep_declaration: { expires: false },

  // Custom - user decides
  custom: { expires: false },

  _default: { expires: false },
};

// Helpers
export function normalizeDocKind(k) {
  const s = String(k || "")
    .toLowerCase()
    .trim();
  const hit = DOC_KINDS.find((d) => d.key === s);
  return hit ? hit.key : "custom";
}

export function getDocKindConfig(kind) {
  return DOC_KIND_FIELDS[normalizeDocKind(kind)] || DOC_KIND_FIELDS.custom;
}

export function listDocKinds() {
  return {
    kinds: DOC_KINDS,
    fields: DOC_KIND_FIELDS,
    expiryPolicies: DOC_EXPIRY_POLICIES,
  };
}

// Group document kinds by category for better UX
export function getDocKindsByCategory() {
  const grouped = {};
  DOC_KINDS.forEach((doc) => {
    if (!grouped[doc.category]) {
      grouped[doc.category] = [];
    }
    grouped[doc.category].push(doc);
  });
  return grouped;
}

// Get field visibility for a document kind
export function getVisibleFields(kind) {
  const config = getDocKindConfig(kind);
  const required = config.required || [];
  const optional = config.optional || [];

  return {
    required,
    optional,
    all: [...required, ...optional],
    shows: {
      identifier: required.includes("identifier") || optional.includes("identifier"),
      issued_on: required.includes("issued_on") || optional.includes("issued_on"),
      expires_on: required.includes("expires_on") || optional.includes("expires_on"),
      period_from: required.includes("period_from") || optional.includes("period_from"),
      period_to: required.includes("period_to") || optional.includes("period_to"),
      authority: required.includes("authority") || optional.includes("authority"),
      status: required.includes("status") || optional.includes("status"),
      note: required.includes("note") || optional.includes("note"),
      file: required.includes("file") || optional.includes("file"),
      kind_label: required.includes("kind_label"),
    },
  };
}
